import { useEffect, useMemo, useRef, useState } from 'react';
import { useAnimationFrame } from '../useAnimationFrame';
import {
  cueHighlights,
  cueIsActive,
  NowYouTry,
  usePrefersReducedMotion,
  useMountSequence,
  VizCard,
  type VizCue,
} from '../vizShared';

// GasLaws — interactive ideal gas law PV = nRT. Pick which variable to SOLVE for,
// then drag the other three; the solved value updates and an animated particle
// box shows the consequences: particle speed scales with √T, the box height (and
// piston) scales with V, and wall-hit frequency rises with P. Demonstrates
// Boyle's, Charles's, Gay-Lussac's and the combined gas law in one model.
//
// config: { p?, v?, n?, t?, solveFor?, title? } — pressure (atm), volume (L),
// moles, temperature (K). Resilient: defaults to 1 atm, 22.4 L, 1 mol, 273 K.

export interface GasLawsConfig {
  title?: string;
  p?: number; // atm
  v?: number; // L
  n?: number; // mol
  t?: number; // K
  solveFor?: 'P' | 'V' | 'n' | 'T';
}

interface Props {
  config?: GasLawsConfig;
  onComplete?: (result?: unknown) => void;
  cue?: VizCue;
}

const R = 0.08206; // L·atm·mol⁻¹·K⁻¹

type Var = 'P' | 'V' | 'n' | 'T';

const RANGES: Record<Var, { min: number; max: number; step: number; unit: string; label: string }> = {
  P: { min: 0.2, max: 10, step: 0.1, unit: 'atm', label: 'Pressure' },
  V: { min: 1, max: 50, step: 0.5, unit: 'L', label: 'Volume' },
  n: { min: 0.2, max: 5, step: 0.1, unit: 'mol', label: 'Moles' },
  T: { min: 100, max: 1000, step: 5, unit: 'K', label: 'Temp' },
};

// Named gas laws → which variable they solve for and which to manipulate (for cues).
const LAWS: Record<string, { solveFor: Var; drive: Var }> = {
  boyle: { solveFor: 'P', drive: 'V' }, // P ∝ 1/V at fixed n,T
  charles: { solveFor: 'V', drive: 'T' }, // V ∝ T at fixed n,P
  'gay-lussac': { solveFor: 'P', drive: 'T' }, // P ∝ T at fixed n,V
  gaylussac: { solveFor: 'P', drive: 'T' },
  combined: { solveFor: 'P', drive: 'V' },
};

function clampRange(v: Var, x: number) {
  const r = RANGES[v];
  if (!Number.isFinite(x)) return r.min;
  return Math.min(r.max, Math.max(r.min, x));
}

const BOX_W = 180;
const BOX_H = 200;

interface Particle { x: number; y: number; vx: number; vy: number; }

export default function GasLaws({ config = {}, onComplete, cue }: Props) {
  const reduced = usePrefersReducedMotion();
  const hasCue = cueIsActive(cue);

  const [solveFor, setSolveFor] = useState<Var>(config.solveFor ?? 'P');
  const [p, setP] = useState(clampRange('P', config.p ?? 1));
  const [v, setV] = useState(clampRange('V', config.v ?? 22.4));
  const [n, setN] = useState(clampRange('n', config.n ?? 1));
  const [t, setT] = useState(clampRange('T', config.t ?? 273));
  const interactedRef = useRef(false);

  const mark = () => {
    if (!interactedRef.current) {
      interactedRef.current = true;
      onComplete?.({ type: 'gas_laws' });
    }
  };

  // ---- Self-demo: Boyle — compress the volume, watch the solved pressure rise.
  const demoStartV = clampRange('V', config.v ?? 22.4);
  const demo = useMountSequence(
    [
      { at: 200, run: () => { setSolveFor('P'); setV(RANGES.V.max * 0.85); } },
      { at: 700, run: () => setV(RANGES.V.max * 0.55) },
      { at: 1100, run: () => setV(RANGES.V.max * 0.3) },
      { at: 1500, run: () => setV(RANGES.V.min + 2) },
      { at: 2000, run: () => { setSolveFor(config.solveFor ?? 'P'); setV(demoStartV); } },
    ],
    { skip: reduced || hasCue, onSettle: () => { setSolveFor(config.solveFor ?? 'P'); setV(demoStartV); } },
  );
  const cancelDemo = () => { if (demo.running) demo.cancel(); };

  // ---- Cue: highlight → named law; step → cycle solveFor; progress → drive var.
  useEffect(() => {
    const hi = cueHighlights(cue);
    if (hi.length === 0) return;
    for (const h of hi) {
      const law = LAWS[h.replace(/[^a-z-]/g, '')];
      if (law) { setSolveFor(law.solveFor); return; }
      const asVar = h.toUpperCase();
      if (asVar === 'P' || asVar === 'V' || asVar === 'N' || asVar === 'T') {
        setSolveFor(asVar === 'N' ? 'n' : (asVar as Var));
        return;
      }
    }
  }, [cue]);

  useEffect(() => {
    if (cue?.step == null) return;
    const order: Var[] = ['P', 'V', 'n', 'T'];
    setSolveFor(order[Math.max(0, Math.min(order.length - 1, Math.round(cue.step)))]);
  }, [cue?.step]);

  useEffect(() => {
    if (cue?.progress == null) return;
    // Drive the first manipulable (non-solved) variable across its range.
    const order: Var[] = ['V', 'P', 'T', 'n'];
    const drive = order.find((x) => x !== solveFor) ?? 'V';
    const r = RANGES[drive];
    const val = r.min + Math.max(0, Math.min(1, cue.progress)) * (r.max - r.min);
    ({ P: setP, V: setV, n: setN, T: setT } as Record<Var, (x: number) => void>)[drive](clampRange(drive, val));
  }, [cue?.progress, solveFor]);

  // Solve the chosen variable from the other three so PV = nRT always holds.
  const solved = useMemo(() => {
    switch (solveFor) {
      case 'P': return (n * R * t) / v;
      case 'V': return (n * R * t) / p;
      case 'n': return (p * v) / (R * t);
      case 'T': return (p * v) / (n * R);
    }
  }, [solveFor, p, v, n, t]);

  // Effective values (the solved one overrides its slider state for display/anim).
  const P = solveFor === 'P' ? solved : p;
  const V = solveFor === 'V' ? solved : v;
  const N = solveFor === 'n' ? solved : n;
  const T = solveFor === 'T' ? solved : t;

  // Particle count ∝ moles (capped for performance/legibility).
  const count = Math.max(4, Math.min(40, Math.round(N * 8)));

  const particles = useMemo<Particle[]>(() => {
    const arr: Particle[] = [];
    for (let i = 0; i < count; i++) {
      arr.push({ x: rand(10, BOX_W - 10), y: rand(10, BOX_H - 10), vx: rand(-1, 1), vy: rand(-1, 1) });
    }
    return arr;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [count]);

  const partsRef = useRef<Particle[]>(particles);
  partsRef.current = particles;
  const [, force] = useState(0);

  // Box gas region height scales with V (piston position).
  const fillFrac = Math.min(1, Math.max(0.12, (V - RANGES.V.min) / (RANGES.V.max - RANGES.V.min)));
  const gasTop = BOX_H * (1 - fillFrac);

  useAnimationFrame((dt) => {
    // speed ∝ √T (kinetic theory); normalize against 273 K baseline.
    const speed = 40 * Math.sqrt(T / 273);
    const top = BOX_H * (1 - Math.min(1, Math.max(0.12, (V - RANGES.V.min) / (RANGES.V.max - RANGES.V.min))));
    for (const pt of partsRef.current) {
      pt.x += pt.vx * speed * dt;
      pt.y += pt.vy * speed * dt;
      if (pt.x < 6) { pt.x = 6; pt.vx = Math.abs(pt.vx); }
      if (pt.x > BOX_W - 6) { pt.x = BOX_W - 6; pt.vx = -Math.abs(pt.vx); }
      if (pt.y < top + 6) { pt.y = top + 6; pt.vy = Math.abs(pt.vy); }
      if (pt.y > BOX_H - 6) { pt.y = BOX_H - 6; pt.vy = -Math.abs(pt.vy); }
    }
    force((x) => (x + 1) % 1000);
  }, !reduced);

  // Warm/cool tint with temperature for visual feedback.
  const tFrac = (T - RANGES.T.min) / (RANGES.T.max - RANGES.T.min);
  const gasColor = `rgb(${Math.round(120 + tFrac * 135)}, ${Math.round(150 - tFrac * 70)}, ${Math.round(230 - tFrac * 150)})`;

  const sliderVars: Var[] = (['P', 'V', 'n', 'T'] as Var[]).filter((x) => x !== solveFor);
  const setter: Record<Var, (x: number) => void> = { P: setP, V: setV, n: setN, T: setT };
  const stateVal: Record<Var, number> = { P, V, n: N, T };

  return (
    <VizCard
      reducedMotion={reduced}
      eyebrow="Ideal gas law"
      title={config.title ?? 'Ideal gas law'}
      subtitle="Solve for one variable, drag the rest — particles, piston and tint respond"
      hint="Choose a variable to solve, drag sliders · ←/→ to adjust · auto-runs a Boyle demo on load"
      actions={<span className="font-mono text-sm font-semibold text-ws-accent">PV = nRT</span>}
    >
      <div className="relative w-full select-none">
        <NowYouTry show={demo.done && !reduced && !hasCue} />

        {/* Solve-for selector */}
        <div className="mb-3 flex items-center gap-1 text-xs">
          <span className="text-ws-ink-3">Solve for:</span>
          {(['P', 'V', 'n', 'T'] as Var[]).map((x) => (
            <button
              key={x}
              type="button"
              onClick={() => { cancelDemo(); setSolveFor(x); mark(); }}
              className={`rounded-sm px-2 py-1 font-mono font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ws-accent focus-visible:ring-offset-1 ${
                solveFor === x ? 'bg-ws-accent text-ws-on-accent' : 'bg-ws-tile text-ws-ink-2 hover:bg-ws-surface-2'
              }`}
              aria-pressed={solveFor === x}
              aria-label={`Solve for ${RANGES[x].label}`}
            >
              {x}
            </button>
          ))}
        </div>

        <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
          {/* Particle box with piston */}
          <div className="flex-shrink-0">
            <svg viewBox={`0 0 ${BOX_W} ${BOX_H + 4}`} className="w-40" role="img" aria-label="Gas particle box">
              {/* cylinder walls */}
              <rect x="1" y="1" width={BOX_W - 2} height={BOX_H} rx="4" fill="var(--ws-surface-2)" stroke="var(--ws-border-strong)" strokeWidth="2" />
              {/* gas region */}
              <rect x="3" y={gasTop} width={BOX_W - 6} height={BOX_H - gasTop - 2} fill={gasColor} opacity="0.18" style={{ transition: reduced ? undefined : 'y 300ms ease-out, height 300ms ease-out, fill 300ms ease-out' }} />
              {/* piston */}
              <rect x="1" y={gasTop - 6} width={BOX_W - 2} height="6" fill="var(--ws-ink-4)" style={{ transition: reduced ? undefined : 'y 300ms ease-out' }} />
              <line x1={BOX_W / 2} y1={Math.max(0, gasTop - 18)} x2={BOX_W / 2} y2={gasTop - 6} stroke="var(--ws-ink-4)" strokeWidth="3" style={{ transition: reduced ? undefined : 'y1 300ms ease-out, y2 300ms ease-out' }} />
              {/* particles */}
              {partsRef.current.map((pt, i) => (
                <circle key={i} cx={pt.x} cy={pt.y} r="4" fill={gasColor} stroke="rgba(0,0,0,0.25)" strokeWidth="0.75" />
              ))}
            </svg>
            <div className="mt-1 text-center text-[10px] text-ws-ink-3">speed ∝ √T · height ∝ V · particles ∝ n</div>
          </div>

          {/* Sliders + solved readout */}
          <div className="min-w-0 flex-1">
            {sliderVars.map((x) => {
              const r = RANGES[x];
              return (
                <div key={x} className="mb-2">
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-ws-ink-2">{r.label} ({x})</span>
                    <span className="font-mono font-semibold tabular-nums text-ws-ink">{fmt(stateVal[x])} {r.unit}</span>
                  </div>
                  <input
                    type="range"
                    min={r.min}
                    max={r.max}
                    step={r.step}
                    value={stateVal[x]}
                    onPointerDown={cancelDemo}
                    onKeyDown={cancelDemo}
                    onChange={(e) => { setter[x](clampRange(x, parseFloat(e.target.value))); mark(); }}
                    className="w-full accent-ws-accent"
                    aria-label={`${r.label} in ${r.unit}`}
                    aria-valuetext={`${fmt(stateVal[x])} ${r.unit}`}
                  />
                </div>
              );
            })}

            {/* Solved variable */}
            <div className="mt-2 rounded-md border border-ws-accent bg-ws-accent-quiet px-3 py-2">
              <div className="text-[10px] uppercase tracking-wide text-ws-accent-ink">Solved · {RANGES[solveFor].label}</div>
              <div className="font-mono text-lg font-bold text-ws-accent-ink">
                {solveFor} = {fmt(solved)} {RANGES[solveFor].unit}
              </div>
            </div>
          </div>
        </div>

        {/* Verify line */}
        <div className="mt-3 grid grid-cols-2 gap-2 text-xs sm:grid-cols-2">
          <div className="rounded-md border border-ws-border bg-ws-tile px-2.5 py-1.5 font-mono tabular-nums text-ws-ink-2">
            PV = {fmt(P * V)} L·atm
          </div>
          <div className="rounded-md border border-ws-border bg-ws-tile px-2.5 py-1.5 font-mono tabular-nums text-ws-ink-2">
            nRT = {fmt(N * R * T)} L·atm
          </div>
        </div>
        <p className="mt-2 text-xs text-ws-ink-3">
          Hold two variables fixed and watch the law specialize: P↔V (Boyle), V↔T (Charles), P↔T (Gay-Lussac). R = 0.08206 L·atm·mol⁻¹·K⁻¹.
        </p>
      </div>
    </VizCard>
  );
}

function fmt(x: number) {
  if (!Number.isFinite(x)) return '—';
  if (Math.abs(x) >= 100) return x.toFixed(0);
  if (Math.abs(x) >= 1) return x.toFixed(2);
  return x.toFixed(3);
}
function rand(min: number, max: number) {
  return min + Math.random() * (max - min);
}
